import type React from 'react';
import { useState } from 'react';
import { Key, Trash2, Plus, Copy, Check } from 'lucide-react';
import {
  PageHeader, PageTitle, Card, Table, Thead, Tbody,
  PrimaryButton, DestructiveButton, SecondaryButton,
  ModalOverlay, ModalDialog, ModalTitle, ModalActions,
  FormGroup, Input, AlertBanner, Mono,
} from '../styled-components';
import { ConfirmModal, EmptyState, SkeletonRows } from '../components';
import { useFetchData } from '../hooks';
import { showToast } from '../hooks';
import { getCSRFToken, CSRF_HEADER_NAME, fetchOrThrow } from '../../common/utils';

interface ApiKey {
  id: string;
  display_name: string;
  redacted_key: string;
  created_at: string;
  created_by_email: string | null;
  last_used_at: string | null;
}

function formatDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Create Key Modal
// ---------------------------------------------------------------------------

interface NewKey { key: string; display_name: string }

function CreateKeyModal({
  onClose, onCreated,
}: { onClose: () => void; onCreated: (key: NewKey) => void }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetchOrThrow('/dashboard/api/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [CSRF_HEADER_NAME]: await getCSRFToken(),
        },
        body: JSON.stringify({ display_name: name }),
      });
      const data = await res.json();
      onCreated({ key: data.key, display_name: data.display_name });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create key');
      setLoading(false);
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Create API Key</ModalTitle>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Key Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Production key"
              autoFocus
              required
            />
          </FormGroup>
          {error && <div style={{ color: 'var(--accent-red)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <ModalActions>
            <SecondaryButton type="button" onClick={onClose} disabled={loading}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading || !name}>
              {loading ? 'Creating…' : 'Create Key'}
            </PrimaryButton>
          </ModalActions>
        </form>
      </ModalDialog>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// New key reveal
// ---------------------------------------------------------------------------



function NewKeyReveal({ newKey, onClose }: { newKey: NewKey; onClose: () => void }) {
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedExport, setCopiedExport] = useState(false);
  const exportCmd = `export GUAVA_API_KEY="${newKey.key}"`;

  return (
    <ModalOverlay>
      <ModalDialog>
        <ModalTitle>API Key Created</ModalTitle>
        <AlertBanner variant="amber">
          Copy your key now — it won't be shown again.
        </AlertBanner>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {/* Raw key */}
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--bg-border)' }}>
            <div style={{ flex: 1, background: 'var(--bg-base)', padding: '9px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--accent-cyan)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {newKey.key}
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(newKey.key); setCopiedKey(true); setTimeout(() => setCopiedKey(false), 2000); }}
              style={{ background: 'var(--bg-surface)', border: 'none', borderLeft: '1px solid var(--bg-border)', cursor: 'pointer', padding: '0 14px', color: copiedKey ? 'var(--accent-cyan)' : 'var(--text-secondary)', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5, transition: 'color 150ms' }}
            >
              {copiedKey ? <Check size={12} /> : <Copy size={12} />} {copiedKey ? 'Copied!' : 'Copy key'}
            </button>
          </div>

          {/* Shell export */}
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--bg-border)' }}>
            <div style={{ flex: 1, background: 'var(--bg-base)', padding: '9px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span style={{ color: 'var(--text-muted)' }}>export </span>
              <span style={{ color: 'var(--accent-yellow)' }}>GUAVA_API_KEY</span>
              <span style={{ color: 'var(--text-muted)' }}>=&quot;</span>
              <span style={{ color: 'var(--accent-cyan)' }}>{newKey.key}</span>
              <span style={{ color: 'var(--text-muted)' }}>&quot;</span>
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(exportCmd); setCopiedExport(true); setTimeout(() => setCopiedExport(false), 2000); }}
              style={{ background: 'var(--bg-surface)', border: 'none', borderLeft: '1px solid var(--bg-border)', cursor: 'pointer', padding: '0 14px', color: copiedExport ? 'var(--accent-cyan)' : 'var(--text-secondary)', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5, transition: 'color 150ms' }}
            >
              {copiedExport ? <Check size={12} /> : <Copy size={12} />} {copiedExport ? 'Copied!' : 'Copy export'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', paddingLeft: 2 }}>
            Paste the export command in your terminal to set the environment variable.
          </div>
        </div>

        <ModalActions>
          <PrimaryButton onClick={onClose}>Done</PrimaryButton>
        </ModalActions>
      </ModalDialog>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// API Keys page
// ---------------------------------------------------------------------------

export function ApiKeysPage() {
  const { data: keys, loading, refetch } = useFetchData<ApiKey[]>('/dashboard/api/api-keys', []);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState<NewKey | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ApiKey | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await fetchOrThrow(`/dashboard/api/api-keys/${confirmDelete.id}`, {
        method: 'DELETE',
        headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
      showToast('API key deactivated', 'success');
      refetch();
    } catch {
      showToast('Failed to deactivate key', 'error');
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  }

  return (
    <div>
      <PageHeader>
        <PageTitle>API Keys</PageTitle>
        <PrimaryButton onClick={() => setShowCreate(true)}>
          <Plus size={16} /> Create API Key
        </PrimaryButton>
      </PageHeader>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table>
          <Thead>
            <tr>
              <th>Name</th>
              <th>Key</th>
              <th>Created By</th>
              <th>Created</th>
              <th>Last Used</th>
              <th></th>
            </tr>
          </Thead>
          <Tbody>
            {loading ? (
              <SkeletonRows cols={6} />
            ) : keys.length === 0 ? (
              <tr><td colSpan={6}>
                <EmptyState
                  icon={<Key size={40} />}
                  title="No API keys yet"
                  body="Create an API key to start building with Guava."
                  action={{ label: 'Create API Key', onClick: () => setShowCreate(true) }}
                />
              </td></tr>
            ) : keys.map((key) => (
              <tr key={key.id}>
                <td style={{ fontWeight: 500 }}>{key.display_name || 'Untitled'}</td>
                <td><Mono>{key.redacted_key}</Mono></td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{key.created_by_email ?? '—'}</td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{formatDate(key.created_at)}</td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{formatDate(key.last_used_at)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <DestructiveButton
                    style={{ padding: '5px 10px', fontSize: 13 }}
                    onClick={() => setConfirmDelete(key)}
                  >
                    <Trash2 size={14} /> Deactivate
                  </DestructiveButton>
                </td>
              </tr>
            ))}
          </Tbody>
        </Table>
      </Card>

      {showCreate && (
        <CreateKeyModal
          onClose={() => setShowCreate(false)}
          onCreated={(k) => { setShowCreate(false); setNewKey(k); refetch(); }}
        />
      )}

      {newKey && <NewKeyReveal newKey={newKey} onClose={() => setNewKey(null)} />}

      {confirmDelete && (
        <ConfirmModal
          title={`Deactivate "${confirmDelete.display_name}"?`}
          body="This key will stop working immediately. This cannot be undone."
          confirmLabel="Deactivate"
          destructive
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
